import { useEffect, useRef, useState } from 'react';

/**
 * 錄音網址會過期（SRA R11：後端給 10 分鐘簽章網址）。
 * 頁面開著超過 10 分鐘再按播放 → <audio> 會 error → 這裡呼叫 refresh() 拿新網址、接著原本的位置播。
 * 每個 src 只重拿一次，避免 refresh 也失敗時無限重試。
 *
 * 用法：
 *   const fresh = useFreshAudioSrc(src, refresh);
 *   <audio ref={audioRef} src={fresh.src} onError={fresh.onError}
 *          onLoadedMetadata={(e) => { fresh.onLoadedMetadata(e); ...原本的處理 }} />
 */
export function useFreshAudioSrc(src, refresh) {
    const [cur, setCur] = useState(src);
    const tried = useRef(false);
    const hadMetadata = useRef(false);
    const resume = useRef(null);

    useEffect(() => {
        setCur(src);
        tried.current = false;
        hadMetadata.current = false;
        resume.current = null;
    }, [src]);

    async function onError(e) {
        if (!refresh || tried.current) return;
        tried.current = true;
        const a = e.currentTarget;
        // 已經載過 metadata 才出錯 = 使用者在播 / 拖進度時網址過期 → 換完接著播
        resume.current = { at: a?.currentTime || 0, play: hadMetadata.current };
        try {
            const next = await refresh();
            if (next && next !== cur) setCur(next);
        } catch (_) {
            /* 拿不到就維持播不了，不重試 */
        }
    }

    function onLoadedMetadata(e) {
        hadMetadata.current = true;
        const r = resume.current;
        if (!r) return;
        resume.current = null;
        const a = e.currentTarget;
        if (r.at) a.currentTime = r.at;
        if (r.play) a.play().catch(() => {});
    }

    return { src: cur, onError, onLoadedMetadata };
}
